import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { ProductSetType } from '@econtractor/types';
import * as apiProductSet from 'api/apiProductSets';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { useProductSet } from '../context/ProductSetProvider';
import useCatalogCategoryFilter from '../../CatalogCategory/model/useCatalogCategoryFilter';

const useProductsSets = () => {
    const { t } = useTranslation(['Sets', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const { showSpinner, setShowSpinner, setsDesc, setsOrder, setSetsOrder, setSetsDesc, listResetNonce, listRefreshNonce } = useProductSet();

    const [setsList, setSetsList] = useState<ProductSetType[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [editSetId, setEditSetId] = useState<number | null>(null);
    const { filterCategoryIds, setFilterCategoryIds } = useCatalogCategoryFilter('set');

    const location = useLocation();
    const onPage = useListOnPage();
    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchSets = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return null;

        const listData = {
            search,
            order: `${setsOrder ? `order by ${setsOrder} ` : ''}${setsDesc ? 'DESC' : ''}`,
            page: page < 0 ? 1 : page || 1,
            onPage,
            categoryIds: filterCategoryIds.length ? filterCategoryIds : undefined,
        };

        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const resSets = await apiProductSet.getList(listData);
        setShowSpinner(false);

        if (!resSets) return showToast(t('common:messError'), 'danger');

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resSets.paginator,
        }));

        setSetsList(resSets.sets);
    };

    const resetSetsList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setSetsOrder('');
        setSetsDesc(false);
        setFilterCategoryIds([]);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            order: '',
            page: 1,
            onPage,
        };

        setShowSpinner(true);
        const resSets = await apiProductSet.getList(listData);
        setShowSpinner(false);

        if (!resSets) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }

        prevListData.current = JSON.stringify(listData);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: resSets.paginator,
        }));

        setSetsList(resSets.sets);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, setSetsDesc, setSetsOrder, setShowSpinner, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetSetsList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchSets(true);
    });

    useEffect(() => {
        if (page === 1) fetchSets(true);
        else setPage(1);
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        fetchSets();
    }, [page, setsOrder, setsDesc, onPage, filterCategoryIds]);

    useEffect(() => {
        const _page = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== _page) setPage(_page);
    }, [g?.paginator?.[location.pathname]?.currentPage, g?.paginator?.[location.pathname]?.countRows, g?.paginator?.[location.pathname]?.onPage]);

    return {
        setsList,
        editSetId,
        setEditSetId,
        fetchSets,
        filterCategoryIds,
        setFilterCategoryIds,
    };
};

export default useProductsSets;
